import { Head } from "vite-react-ssg";
import {
  SUPPORTED_LANGUAGES,
  directionFor,
  localeUrl,
  type SupportedLanguage,
} from "@/lib/site";

interface SeoProps {
  lang: SupportedLanguage;
  title: string;
  description: string;
  /** Absolute canonical URL for the *current* route (usually `localeUrl(lang)`). */
  canonical: string;
}

/**
 * Per-route document head: <html lang/dir>, <title>, description, canonical, AR<->EN hreflang
 * alternates, and Open Graph tags. Rendered through vite-react-ssg's <Head> (a thin wrapper
 * around react-helmet-async), so everything here is baked into the static .html file emitted for
 * each route at build time — not injected client-side only.
 */
export function Seo({ lang, title, description, canonical }: SeoProps) {
  const dir = directionFor(lang);

  return (
    <Head>
      <html lang={lang} dir={dir} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {SUPPORTED_LANGUAGES.map((altLang) => (
        <link
          key={altLang}
          rel="alternate"
          hrefLang={altLang}
          href={localeUrl(altLang)}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={localeUrl("ar")} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={lang === "ar" ? "ar_EG" : "en_US"} />
      <meta
        property="og:locale:alternate"
        content={lang === "ar" ? "en_US" : "ar_EG"}
      />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Head>
  );
}
